
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Download, Plus } from 'lucide-react';
import { ImportCertificateNumbersDialog } from '@/components/admin/certificates/ImportCertificateNumbersDialog';
import { CertificateInventoryList } from '@/components/admin/certificates/CertificateInventoryList';
import { ImportBatchesList } from '@/components/admin/certificates/ImportBatchesList';
import { VerificationChecklist } from '@/components/admin/certificates/VerificationChecklist';
import { CertificateExportModal } from '@/components/admin/certificates/CertificateExportModal';
import { CertificateDetailView } from '@/components/admin/certificates/CertificateDetailView';
import { CertificateAuditTrail } from '@/components/admin/certificates/CertificateAuditTrail';
import { ComplianceNotificationSystem } from '@/components/admin/certificates/ComplianceNotificationSystem';
import { useIsMobile } from '@/hooks/use-mobile';
import { ResponsiveTable } from '@/components/ui/responsive-table';

export function CertificatesInventoryContent() {
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("inventory");
  const [selectedCertificateNumber, setSelectedCertificateNumber] = useState<string>('');
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const isMobile = useIsMobile();
  
  const handleImportSuccess = () => {
    // Force refetch data when a new import is successful
  };
  
  const handleCertificateSelect = (certificateNumber: string) => {
    setSelectedCertificateNumber(certificateNumber);
    setIsDetailViewOpen(true);
  };
  
  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <h1 className="text-xl md:text-2xl font-bold">Certificate Inventory</h1>
        <div className="flex flex-wrap items-center gap-2">
          <ComplianceNotificationSystem />
          
          <Button 
            variant="outline" 
            onClick={() => setIsExportModalOpen(true)}
            className="flex items-center gap-2"
            size={isMobile ? "sm" : "default"}
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
          
          <Button 
            onClick={() => setIsImportDialogOpen(true)} 
            className="flex items-center gap-2 flex-grow md:flex-grow-0"
            size={isMobile ? "sm" : "default"}
          >
            <Plus className="h-4 w-4" />
            Import Certificate Numbers
          </Button>
        </div>
      </div>
      
      <Tabs defaultValue="inventory" className="w-full" onValueChange={setActiveTab}>
        <TabsList className={`flex flex-wrap gap-1 md:gap-0 ${!isMobile ? 'grid grid-cols-4 w-[800px]' : 'w-full'}`}>
          <TabsTrigger value="inventory" className="whitespace-nowrap">Certificate Inventory</TabsTrigger>
          <TabsTrigger value="verification" className="whitespace-nowrap">Verification</TabsTrigger>
          <TabsTrigger value="audit" className="whitespace-nowrap">Audit Trail</TabsTrigger>
          <TabsTrigger value="batches" className="whitespace-nowrap">Import Batches</TabsTrigger>
        </TabsList>
        
        <TabsContent value="inventory" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Certificate Numbers</CardTitle>
              <CardDescription>Manage certificate numbers available for issuance</CardDescription>
            </CardHeader>
            <CardContent>
              <CertificateInventoryList onCertificateSelect={handleCertificateSelect} />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="verification" className="mt-6">
          <VerificationChecklist />
        </TabsContent>
        
        <TabsContent value="audit" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Certificate Audit Trail</CardTitle>
              <CardDescription>View the complete history of all certificate activities</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveTable compact={isMobile}>
                <CertificateAuditTrail />
              </ResponsiveTable>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="batches" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Import History</CardTitle>
              <CardDescription>View history of certificate number imports</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveTable compact={isMobile}>
                <ImportBatchesList />
              </ResponsiveTable>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      <ImportCertificateNumbersDialog 
        open={isImportDialogOpen} 
        onOpenChange={setIsImportDialogOpen} 
        onSuccess={handleImportSuccess}
      />
      
      <CertificateExportModal
        open={isExportModalOpen}
        onOpenChange={setIsExportModalOpen}
      />
      
      <CertificateDetailView
        certificateNumber={selectedCertificateNumber}
        open={isDetailViewOpen}
        onOpenChange={setIsDetailViewOpen}
      />
    </div>
  );
}
